/**
 * Unified Date/Time Utilities
 *
 * The single home for date and time formatting in the app. Date-only strings
 * come from Postgres `date` columns (practices.date, games.date,
 * game_plans.game_date, ...) and must be parsed as local — see `parseLocalDate`.
 *
 * Add a formatter here only when something actually calls it.
 */

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a date string to a Date anchored at **local** midnight.
 *
 * `new Date('2026-03-15')` parses an ISO date-only string as *UTC* midnight, so
 * rendering it with `toLocaleDateString()` shows the previous day for anyone at
 * a negative UTC offset. Building the Date from its components keeps it on the
 * intended calendar day in every timezone. Strings carrying an explicit time are
 * handed to the normal Date parser untouched.
 */
const parseLocalDate = (dateString: string): Date => {
  const parts = DATE_ONLY_PATTERN.exec(dateString);
  if (!parts) return new Date(dateString);

  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
};

/** Format a date with the given Intl options, falling back to the raw input. */
const formatWith = (
  dateString: string,
  options: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) return '';

  const date = parseLocalDate(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString('en-US', options);
};

/**
 * Format date as MM/DD/YYYY — e.g. "03/15/2026".
 */
export const formatDate = (dateString: string): string =>
  formatWith(dateString, { month: '2-digit', day: '2-digit', year: 'numeric' });

/**
 * Format date with the weekday spelled out — e.g. "Sunday, March 15, 2026".
 * Used on the public share pages.
 */
export const formatDateWithWeekday = (dateString: string): string =>
  formatWith(dateString, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

/**
 * Format a time with 12h/24h option. Accepts "HH:MM" or "HH:MM:SS" strings as
 * well as a Date.
 */
export const formatTime = (time: Date | string, format: '12h' | '24h' = '12h'): string => {
  if (!time) return '';

  try {
    const timeString = time instanceof Date
      ? time.toTimeString().slice(0, 5) // HH:MM
      : time;

    const [hours, minutes] = timeString.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return typeof time === 'string' ? time : '';
    }

    if (format === '12h') {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return typeof time === 'string' ? time : '';
  }
};

/**
 * Format minutes since practice start as a clock time.
 * Used for drill start/end times relative to the practice start.
 */
export const formatMinutesSincePracticeStart = (
  minutes: number,
  startTime: string,
  timeFormat: '12h' | '24h' = '12h'
): string => {
  if (minutes === null || minutes === undefined || !startTime) return '';

  try {
    const [hours, mins] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;

    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;

    return formatTime(
      `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`,
      timeFormat
    );
  } catch (error) {
    console.error('Error formatting minutes since practice start:', error);
    return '';
  }
};
